
      let exampleText = String.raw`---
title: "Making of GPT-6 Astra"
authors:
  - name: Astra Systems Research (Fictional)
abstract: |
  We document the fictional training lifecycle of GPT-6 Astra, a sparse mixture-of-experts language model optimized for long-context reasoning and reliable tool use. The study connects every manuscript claim to synthetic runs, checkpoints, evaluation artifacts, and negative results.
---

# Making of GPT-6 Astra

## Abstract

We present **GPT-6 Astra**, a fictional frontier-model program built to test whether sparse routing, long-context curriculum learning, and tool-aware post-training can coexist without turning inference into a small electrical weather event. The candidate activates 31B of 126B parameters per token and is evaluated on reasoning, code repair, browser tool use, calibration, and long-context retrieval. The manuscript currently reports **76.9% on the Astra Reasoning Index**; the locked evaluation later reaches 78.4%. Everything here is synthetic. The model, results, launch timing, and suspiciously dramatic codename exist only to demonstrate a reproducible AI research workflow.

## 1. Motivation

Frontier-model announcements tend to arrive with a benchmark table, a cinematic gradient, and very little lineage. This study keeps the entertaining codename but replaces the fog machine with inspectable runs. Every consequential number is connected to a checkpoint, configuration, evaluation harness, and source revision.

The technical objective is next-token prediction under a fixed active-compute budget, followed by post-training for multi-step reasoning and external tool use. Given token sequence $x_{1:T}$ and router $r_\phi$, the model minimizes autoregressive loss while distributing each token across the top two experts. We test three hypotheses: sparse experts improve capability per active parameter; a staged 8k-to-256k curriculum preserves short-context quality; and execution-grounded preference data reduces confident tool errors.

Our contribution is not a claim that tomorrow's model has leaked. It is a compact, versioned example of what the paper trail behind such a model should look like.

## 2. Training corpus

Pretraining uses **2.4 trillion deduplicated tokens** drawn from synthetic web, code, mathematics, scientific text, and multilingual mixtures. Documents are filtered for duplication, benchmark contamination, secrets, and unsafe executable payloads. Mixture weights are frozen before the final pretraining phase and recorded with the dataset manifest.

The held-out contamination suite contains exact, fuzzy, and semantic matches against all evaluation prompts. Long documents are sampled with position balancing so evidence does not always appear near the beginning of a sequence—a surprisingly effective way to stop a 256k model from behaving like an 8k model wearing a longer trench coat.

## 3. Architecture and optimization

GPT-6 Astra is optimized with AdamW using a peak learning rate of **2.4e-4**. The decoder contains 48 transformer blocks, grouped-query attention, rotary position embeddings, and 16 feed-forward experts per sparse layer. A learned router selects two experts per token, yielding 126B total parameters and 31B active parameters.

The training objective combines language modeling, router load balancing, and z-loss:

$$
\mathcal{L}=\mathcal{L}_{LM}+0.01\mathcal{L}_{balance}+10^{-3}\mathcal{L}_{z}
$$

Sequence length grows from 8k to 64k and finally 256k tokens. We use cosine decay, gradient clipping at 1.0, bfloat16 activations, FP32 optimizer states, and expert-parallel sharding. Every run logs throughput, loss, router entropy, expert utilization, gradient norm, and hardware faults.

## 4. Post-training

Post-training combines supervised reasoning traces, preference optimization, and execution-grounded tool trajectories. Tool calls are evaluated against deterministic sandboxes; a response receives credit only when the resulting state is correct. Failed calls, unnecessary calls, and plausible-looking fabricated outputs are penalized separately.

One router-temperature sweep is deliberately retained as a failed run. It produced excellent benchmark screenshots and catastrophic expert collapse, which is exactly the sort of result that disappears when a project tracks only winners.

## 5. Evaluation protocol

All candidates are evaluated with a frozen harness and prompt set. The primary endpoint is the Astra Reasoning Index, a synthetic aggregate of math reasoning, repository repair, grounded browser tasks, and long-context retrieval. Secondary endpoints include tool-task completion, hallucination rate, 128k needle retrieval, tokens per second, and calibration error.

| Model | Reasoning index | Tool completion | 128k retrieval |
| --- | ---: | ---: | ---: |
| Dense 34B baseline | 63.8% | 58.1% | 71.4% |
| Astra MoE candidate | 76.9% | 81.2% | 92.6% |

![GPT-6 Astra evaluation](demo/astra-evaluation-v1.svg)

The locked run reaches 78.4% on the reasoning index, 84.7% tool completion, 95.1% retrieval, and a 5.8% hallucination rate. The older table and figure intentionally remain attached to the pre-lock candidate so Research Diff has genuine supersession to surface rather than a suspiciously perfect demo.

## 6. Ablations

Removing execution feedback lowers tool completion by 9.6 percentage points while barely changing language-model loss. Disabling the router balance term concentrates 61% of tokens in three experts and increases tail latency. Long-context continuation without position balancing preserves perplexity but loses 17.3 points on evidence retrieval beyond 96k tokens.

## 7. Reproducibility and limitations

Each run stores its source commit, dataset version, configuration, metric history, artifacts, and supersession relationships. The locked evaluator consumes immutable prediction files and emits the summary table and figure from one protocol identifier.

This is a fictional system study, not an OpenAI announcement, leak, release forecast, or statement about an actual GPT-6 model. The synthetic scores are designed to exercise experiment tracking and manuscript verification. They should not be cited anywhere except, perhaps, in a group chat with appropriate levels of disbelief.

## 8. Conclusion

If a frontier model really does arrive tomorrow, the interesting part should be more than the name. The useful artifact is the chain from training decision to run, from run to evidence, and from evidence to claim. Astra is fictional; the provenance problem is not.`;
      window.__demoResearchText = exampleText;
      console.log("Potter's Wheel research workspace loaded.");

      import MystEditor, { defaultButtons } from "./MystEditor.jsx";

      const usercolors = ["#30bced", "#60c771", "#e6aa3a", "#cbb63e", "#ee6352", "#9ac2c9", "#8acb88", "#14b2c4"];

      const urlParams = new URLSearchParams(window.location.search);
      const room = urlParams.get("room") || "0";
      const username = urlParams.get("username") || Math.floor(Math.random() * 1000).toString();
      const color = usercolors[Math.floor(Math.random() * usercolors.length)];
      let exampleCustomRoles = [
        {
          target: "say",
          transform: async (content) => username + " says: '" + content + "'",
        },
      ];

      let exampleCustomDirectives = [
        {
          target: "bold",
          transform: (_, data) => `<b style="white-space: pre-wrap;">${data.body}</b>`,
        },
      ];

      let exampleTransforms = [
        {
          // Other repo issue links
          target: /[0-9a-z\-]+\/[0-9a-z\-]+#\d{1,10}/g,
          transform: (match) => {
            const [repo, issueId] = match.split("#");
            return `<a href="https://github.com/${repo}/issues/${issueId}">${match}</a>`;
          },
        },
        {
          // Other repo PR links
          target: /[0-9a-z\-]+\/[0-9a-z\-]+\!\d+/g,
          transform: (match) => {
            const [repo, prid] = match.split("!");
            return `<a href="https://github.com/${repo}/pull/${prid}">${match}</a>`;
          },
        },
        {
          // Same repo issue links
          target: /(^|(?<=\s))#\d+/g,
          transform: (match) => {
            const issueId = match.slice(1);
            return `<a href="https://github.com/antmicro/myst-editor/issues/${issueId}">${match}</a>`;
          },
        },
        {
          // Same repo PR links
          target: /(^|(?<=\s))!\d+/g,
          transform: (match) => {
            const prid = match.slice(1);
            return `<a href="https://github.com/antmicro/myst-editor/pull/${prid}">${match}</a>`;
          },
        },
        {
          // User links
          target: /@[0-9a-z\-]+/g,
          transform: (match) => {
            const user = match.slice(1);
            return `
          <a href='https://github.com/${user}'>
            ${user}
          </a>`;
          },
        },
        {
          target: /\|date\|/g,
          transform: (match) => new Promise((r) => r(new Date().toLocaleString("en-GB", { timeZone: "UTC" }))),
        },
      ];

      const collabEnabled = !(import.meta.env.VITE_COLLAB == "OFF") && urlParams.get("collab") != "false";
      const collabUrl = import.meta.env.VITE_WS_URL ?? urlParams.get("collab_server");

      MystEditor(
        {
          id: "demo",
          templatelist: "linkedtemplatelist.json",
          initialText: exampleText,
          title: "Making of GPT-6 Astra",
          integrityPanel: true,
          transforms: exampleTransforms,
          collapsibleHeadingMarker: true,
          collaboration: {
            enabled: collabEnabled,
            commentsEnabled: collabEnabled,
            resolvingCommentsEnabled: collabEnabled,
            wsUrl: collabUrl ?? "#",
            username,
            room,
            color,
            mode: collabUrl ? "websocket" : "local",
          },
          customRoles: exampleCustomRoles,
          customDirectives: exampleCustomDirectives,
          includeButtons: defaultButtons,
          spellcheckOpts: { dict: "en_US", dictionaryPath: `${window.location.pathname}dictionaries` },
          syncScroll: true,
        },
        document.getElementById("myst"),
      );
    